// Browser events stop at iframe boundaries. Observe same-origin resource/App
// documents and relay outside interactions to platform documents. Each menu
// keeps its existing LiveView or client-side dismissal handler.
export function installFrameClickAway(win = window) {
  let top = win
  try {
    while (top.parent !== top && top.parent.document) top = top.parent
  } catch (_) { /* Stop at the same-origin boundary. */ }
  if (top.__seivaFrameClickAway) return top.__seivaFrameClickAway.register(win)

  const documents = new WeakSet(), frames = new WeakSet()
  const relay = (source, event) => {
    if (event.seivaFrameClickAway) return
    const visit = destination => {
      try {
        const doc = destination.document
        if (destination !== source && destination.__seivaSharedRuntime) {
          // For an ancestor, the click belongs to its actual iframe. This
          // preserves menus containing an iframe instead of dismissing them.
          let branch = source, target = null
          while (branch !== top && branch.parent !== branch) {
            if (branch.parent === destination) { target = branch.frameElement; break }
            branch = branch.parent
          }
          target ||= doc.querySelector('[phx-hook="AiosShell"]') || doc.body
          if (target) {
            // Synthetic events do not invoke the browser's native light-dismiss.
            for (const popover of doc.querySelectorAll('[popover]:popover-open')) {
              if (popover.popover === 'auto' && !popover.contains(target)) popover.hidePopover()
            }
            const outside = new destination.MouseEvent(event.type, {bubbles: true, detail: 0})
            outside.seivaFrameClickAway = true
            target.dispatchEvent(outside)
          }
        }
        for (let i = 0; i < destination.frames.length; i++) visit(destination.frames[i])
      } catch (_) { /* Cross-origin apps own their interaction handlers. */ }
    }
    visit(top)
  }
  const register = view => {
    try {
      const doc = view.document
      if (documents.has(doc)) return
      documents.add(doc)
      view.addEventListener('pointerdown', event => relay(view, event), true)
      view.addEventListener('click', event => relay(view, event), true)
      const scan = () => {
        for (const frame of doc.querySelectorAll('iframe')) {
          if (!frames.has(frame)) {
            frames.add(frame)
            frame.addEventListener('load', () => register(frame.contentWindow))
          }
          register(frame.contentWindow)
        }
      }
      // App previews need no platform bundle to participate; their host binds
      // the document on load and when a new nested frame is inserted.
      new view.MutationObserver(records => {
        if (records.some(record => [...record.addedNodes].some(node =>
          node.nodeType === 1 && (node.matches('iframe') || node.querySelector('iframe'))
        ))) scan()
      }).observe(doc, {subtree: true, childList: true})
      scan()
    } catch (_) { /* Inaccessible frame. */ }
  }
  top.__seivaFrameClickAway = {register}
  register(top)
}
